import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

import { FILE_ORDERING_FILENAME_EXAMPLES, FILE_ORDERING_TIER } from 'citadel-shared/fileOrdering.js'

import { theme } from '../../themes/theme'
import { FlexColumn } from '../styles/Containers'
import { BoldMedBodyText, SmallBodyText } from '../styles/Text'
import { DRAG_HANDLE, MUTED_TEXT } from '../styles/colors'

export const FILE_ORDERING_TIER_COPY = {
  [FILE_ORDERING_TIER.FILENAME]: {
    label: 'Date in file name',
    description: `e.g. ${FILE_ORDERING_FILENAME_EXAMPLES.join(', ')}`
  },
  [FILE_ORDERING_TIER.DOCUMENT]: {
    label: 'Date found inside the document',
    description: 'First 3 pages; printed dates such as “March 15, 2024” or “Date: 15/03/2024”'
  },
  [FILE_ORDERING_TIER.METADATA]: {
    label: 'File metadata date',
    description: 'PDF creation or modification date; email Date header'
  }
}

const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`

// The whole row is the drag target, so it carries the grab cursor and a hover border — with the
// ▲▼ buttons gone (PR A feedback: drag-and-drop only) those are what keep the row reading as
// reorderable, alongside the handle.
const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px 20px 12px 18px;
  background: ${theme.colors.background.white};
  border: 1px solid ${props => (props.isOver ? theme.colors.primary : 'transparent')};
  border-radius: ${theme.borderRadius.div};
  opacity: ${props => (props.isDragging || props.disabled ? 0.6 : 1)};
  cursor: ${props => (props.disabled ? 'default' : 'grab')};
  transition:
    border-color 0.12s ease,
    opacity 0.12s ease;

  :hover {
    border-color: ${props => {
      if (props.isOver) return theme.colors.primary
      return props.disabled ? 'transparent' : theme.colors.primaryExtraLight
    }};
  }

  :active {
    cursor: ${props => (props.disabled ? 'default' : 'grabbing')};
  }

  :hover .file-ordering-drag-handle {
    color: ${props => (props.disabled ? DRAG_HANDLE : theme.colors.primary)};
  }
`

// With the ▲▼ buttons gone the grip is the row's only persistent affordance, so it rests at
// DRAG_HANDLE (a control colour, ~4.9:1) rather than the muted-copy grey, and fills a 24×24 target.
// `disabled` also kills the focus ring: tabIndex=-1 does not blur a handle that already has focus,
// so a locked list would otherwise still paint one (harness screenshot 08-saving).
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  color: ${DRAG_HANDLE};
  cursor: ${props => (props.disabled ? 'default' : 'grab')};
  transition: color 0.12s ease;

  :hover,
  :focus-visible {
    color: ${props => (props.disabled ? DRAG_HANDLE : theme.colors.primary)};
  }

  :focus-visible {
    outline: 2px solid ${props => (props.disabled ? 'transparent' : theme.colors.primary)};
    outline-offset: 2px;
    border-radius: 2px;
  }

  :active {
    cursor: ${props => (props.disabled ? 'default' : 'grabbing')};
  }

  svg {
    font-size: 22px;
  }
`

const PositionBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: ${theme.colors.primaryExtraLight};
  color: ${theme.colors.primary};
  font-size: 12px;
  font-weight: 600;
`

const Description = styled(SmallBodyText)`
  color: ${MUTED_TEXT};
`

const Copy = styled(FlexColumn)`
  flex: 1;
  gap: 3px;
  min-width: 0;
`

function move(priority, from, to) {
  if (to < 0 || to >= priority.length || from === to) return null
  const next = [...priority]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

// Accessible, controlled priority list with drag and keyboard reordering.
function FileOrderingPriorityList({ priority, onChange, disabled = false }) {
  const dragIndexRef = useRef(null)
  const [dragIndex, setDragIndex] = useState(null)
  const [overIndex, setOverIndex] = useState(null)

  function emitMove(from, to) {
    if (disabled) return
    const next = move(priority, from, to)
    if (next) onChange(next)
  }

  function handleHandleKeyDown(event, index) {
    if (disabled) return
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault()
      emitMove(index, index - 1)
    } else if (event.key === 'ArrowDown' && index < priority.length - 1) {
      event.preventDefault()
      emitMove(index, index + 1)
    }
  }

  function handleDragStart(index) {
    if (disabled) return
    dragIndexRef.current = index
    setDragIndex(index)
  }

  function handleDragOver(event, index) {
    if (disabled) return
    event.preventDefault()
    if (index !== overIndex) setOverIndex(index)
  }

  function handleDrop(index) {
    const from = dragIndexRef.current
    dragIndexRef.current = null
    setDragIndex(null)
    setOverIndex(null)
    if (disabled || from === null) return
    emitMove(from, index)
  }

  function handleDragEnd() {
    dragIndexRef.current = null
    setDragIndex(null)
    setOverIndex(null)
  }

  return (
    <List aria-label="Date sources in priority order">
      {priority.map((tier, index) => {
        const { label, description } = FILE_ORDERING_TIER_COPY[tier] ?? { label: tier, description: '' }
        return (
          <Row
            key={tier}
            data-testid="priority-row"
            data-tier={tier}
            disabled={disabled}
            isDragging={dragIndex === index}
            isOver={overIndex === index && dragIndex !== index}
            draggable={!disabled}
            onDragStart={() => handleDragStart(index)}
            onDragOver={event => handleDragOver(event, index)}
            onDrop={() => handleDrop(index)}
            onDragEnd={handleDragEnd}>
            <DragHandle
              className="file-ordering-drag-handle"
              role="group"
              aria-roledescription="sortable item"
              aria-keyshortcuts="ArrowUp ArrowDown"
              tabIndex={disabled ? -1 : 0}
              disabled={disabled}
              aria-disabled={disabled}
              aria-label={`Reorder ${label}, position ${index + 1} of ${priority.length}. Use the up and down arrow keys to move this source.`}
              onKeyDown={event => handleHandleKeyDown(event, index)}>
              <DragIndicatorIcon />
            </DragHandle>
            <PositionBadge aria-hidden="true">{index + 1}</PositionBadge>
            <Copy>
              <BoldMedBodyText>{label}</BoldMedBodyText>
              <Description>{description}</Description>
            </Copy>
          </Row>
        )
      })}
    </List>
  )
}

FileOrderingPriorityList.propTypes = {
  priority: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default FileOrderingPriorityList
